import { isSignupPath, isSiteMarket, type SignupPath, type SignupTrack } from '~/constants/market';
import { isValidPostAuthRedirect } from '~/utils/authRedirect';

export const REGISTER_PATH = '/auth/register';

// Keys the register page treats as internal, plus the ones this builder owns.
const INTERNAL_KEYS = new Set(['token', 'redirect', 'paywallforce', 'track', 'path', 'market']);
const isInternalKey = (key: string): boolean => INTERNAL_KEYS.has(key.toLowerCase()) || /^paywall_/i.test(key);

const decodeKey = (raw: string): string => {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
};

/** Attribution segments of the current query, kept byte for byte (fbclid/gclid values untouched). */
export const carriedQuerySegments = (search: string): string[] =>
  search
    .replace(/^\?/, '')
    .split('&')
    .filter((segment) => {
      if (!segment) return false;
      const key = decodeKey(segment.split('=')[0] ?? '');
      return key !== '' && !isInternalKey(key);
    });

export type SignupHrefInput = {
  track: SignupTrack;
  path?: SignupPath | null;
  /** Post-auth target; dropped unless it is a valid redirect (not a landing page). */
  redirect?: string | null;
  /** The current page's `location.search`; empty during SSR so cached HTML never carries UTMs. */
  search?: string;
};

export const buildSignupHref = ({ track, path = null, redirect = null, search = '' }: SignupHrefInput): string => {
  const own = [`track=${track}`];
  if (path) own.push(`path=${path}`);
  if (redirect && isValidPostAuthRedirect(redirect)) own.push(`redirect=${encodeURIComponent(redirect)}`);
  return `${REGISTER_PATH}?${[...own, ...carriedQuerySegments(search)].join('&')}`;
};

/** Register page: valid track/path from its query, anything else ignored. */
export const parseSignupTrack = (raw: unknown): SignupTrack | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isSiteMarket(value) ? value : null;
};

export const parseSignupPath = (raw: unknown): SignupPath | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isSignupPath(value) ? value : null;
};
